// ════════════════════════════════════════════════════════════════════════════
// 🛡️ usePermissions — Phase 5 Frontend RBAC
// ════════════════════════════════════════════════════════════════════════════
// Mirrors the backend permission matrix in @c:/…/server/utils/rbac.ts so the UI
// can hide or disable actions the signed-in cashier cannot perform. Backend is
// still the source of truth (enforces via Bearer JWT on every request) — this
// module ONLY drives UX polish (cleaner than showing 403 toasts).
// ════════════════════════════════════════════════════════════════════════════

export type CashierRole = 'manager' | 'cashier' | 'inventory' | 'readonly';

export interface CashierSessionLite {
  id: number;
  storeId: number;
  username: string;
  name: string;
  role: CashierRole;
  roleLabel?: string;
  customPermissions?: string[] | null;
}

// Must stay in sync with server/utils/rbac.ts PERMISSIONS.
const PERMISSIONS: Record<string, CashierRole[]> = {
  'transaction:void': ['manager'],
  'transaction:discount': ['manager', 'cashier'],
  'transaction:checkout': ['manager', 'cashier'],
  'transaction:view': ['manager', 'cashier', 'inventory', 'readonly'],

  'inventory:create': ['manager', 'inventory'],
  'inventory:edit': ['manager', 'inventory'],
  'inventory:delete': ['manager'],
  'inventory:bulk': ['manager', 'inventory'],

  'customer:create': ['manager', 'cashier'],
  'customer:edit': ['manager', 'cashier'],
  'customer:delete': ['manager'],
  'loyalty:config': ['manager'],

  'cashier:create': ['manager'],
  'cashier:edit': ['manager'],
  'cashier:delete': ['manager'],
  'cashier:view': ['manager'],

  'analytics:view': ['manager', 'cashier', 'readonly'],
  'dayend:run': ['manager'],

  'store:settings': ['manager'],
  'store:theme': ['manager'],

  // 🕐 Phase 7 — Shift management
  'shift:manage': ['manager'],
  'shift:view': ['manager', 'cashier', 'inventory', 'readonly'],
};

/**
 * Checks if the given role can perform the given action.
 * If session is null (not signed in), we fall back to PERMISSIVE — legacy pre-RBAC behavior.
 * This prevents regressions for users on the old session format who haven't re-logged in yet.
 */
export function can(session: CashierSessionLite | null, action: string): boolean {
  // Legacy / unsigned — permissive (backend will still gate via 403).
  if (!session) return true;
  if (session.role === 'manager') return true;
  // 🎯 Per-cashier custom permissions override role-based defaults
  if (session.customPermissions && session.customPermissions.length > 0) {
    return session.customPermissions.includes(action);
  }
  const allowed = PERMISSIONS[action];
  if (!allowed) return true; // unknown action — don't block legit features
  return allowed.includes(session.role);
}

/** Returns the current cashier session from localStorage, or null. */
export function getCurrentCashier(): CashierSessionLite | null {
  try {
    // Prefer the Phase 4 / POSDashboard session object (embedded in cashier state).
    // But our actual persistence is the token + a decoded cache is not stored.
    // Easiest path: decode the token payload (no signature verify client-side — backend is the gate).
    const token = localStorage.getItem('kangavena:cashierToken');
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload || !payload.role) return null;
    return {
      id: payload.id,
      storeId: payload.storeId,
      username: payload.username,
      name: payload.name,
      role: payload.role,
      customPermissions: payload.customPermissions || null,
    };
  } catch {
    return null;
  }
}

/**
 * React hook variant — reactive on storage changes so UI updates when cashier
 * switches (Phase 4 CashierAuth). Component callers should pass the explicit
 * cashier session from POSDashboard state when available for best responsiveness;
 * this hook is a fallback for deeply nested components.
 */
import { useEffect, useState } from 'react';
export function usePermissions() {
  const [session, setSession] = useState<CashierSessionLite | null>(() => getCurrentCashier());
  useEffect(() => {
    const onStorage = () => setSession(getCurrentCashier());
    window.addEventListener('storage', onStorage);
    // Also poll every 2s for same-tab changes (localStorage `storage` event only fires across tabs).
    const id = window.setInterval(onStorage, 2000);
    return () => { window.removeEventListener('storage', onStorage); window.clearInterval(id); };
  }, []);
  return {
    session,
    role: session?.role ?? null,
    can: (action: string) => can(session, action),
  };
}
